import { Font } from '@react-pdf/renderer';

export const PDF_FONT_FAMILY_SANS = 'Manrope';
export const PDF_FONT_FAMILY_SERIF = 'Lora';

export const PDF_FONT_FILES = {
  sansRegular: 'manrope-regular.ttf',
  sansSemiBold: 'manrope-semibold.ttf',
  serifItalic: 'lora-italic.ttf',
} as const;

export type PdfFontFileName = (typeof PDF_FONT_FILES)[keyof typeof PDF_FONT_FILES];
export type PdfFontSourceResolver = (fileName: PdfFontFileName) => string;

export interface RegisterPdfFontsOptions {
  sourceResolver?: PdfFontSourceResolver;
}

let fontsRegistered = false;

export function getBrowserPdfFontSource(fileName: PdfFontFileName): string {
  return `/fonts/${fileName}`;
}

export function createNodePdfFontSourceResolver(
  baseUrl = defaultNodeFontBaseUrl(),
): PdfFontSourceResolver {
  return (fileName) => decodeURIComponent(new URL(fileName, baseUrl).pathname);
}

export function registerPdfFonts(options: RegisterPdfFontsOptions = {}): void {
  if (fontsRegistered || arePdfFontsRegistered()) {
    fontsRegistered = true;
    return;
  }

  const resolveSource = options.sourceResolver ?? getBrowserPdfFontSource;

  Font.register({
    family: PDF_FONT_FAMILY_SANS,
    fonts: [
      { src: resolveSource(PDF_FONT_FILES.sansRegular), fontWeight: 400 },
      { src: resolveSource(PDF_FONT_FILES.sansSemiBold), fontWeight: 600 },
    ],
  });
  Font.register({
    family: PDF_FONT_FAMILY_SERIF,
    fonts: [
      { src: resolveSource(PDF_FONT_FILES.serifItalic), fontStyle: 'italic', fontWeight: 400 },
    ],
  });

  fontsRegistered = true;
}

export function resetPdfFontRegistrationForTests(): void {
  Font.clear();
  registerReactPdfStandardFonts();
  fontsRegistered = false;
}

function arePdfFontsRegistered(): boolean {
  const registeredFonts = Font.getRegisteredFonts();
  return PDF_FONT_FAMILY_SANS in registeredFonts && PDF_FONT_FAMILY_SERIF in registeredFonts;
}

function defaultNodeFontBaseUrl(): URL {
  const cwd =
    typeof process !== 'undefined' && typeof process.cwd === 'function' ? process.cwd() : null;
  if (!cwd) return new URL('/fonts/', globalThis.location?.origin ?? 'http://localhost');
  const normalizedCwd = cwd.endsWith('/') ? cwd : `${cwd}/`;
  return new URL(`file://${normalizedCwd}public/fonts/`);
}

function registerReactPdfStandardFonts(): void {
  Font.register({
    family: 'Helvetica',
    fonts: [
      { src: 'Helvetica', fontStyle: 'normal', fontWeight: 400 },
      { src: 'Helvetica-Bold', fontStyle: 'normal', fontWeight: 700 },
      { src: 'Helvetica-Oblique', fontStyle: 'italic', fontWeight: 400 },
      { src: 'Helvetica-BoldOblique', fontStyle: 'italic', fontWeight: 700 },
    ],
  });
  Font.register({
    family: 'Courier',
    fonts: [
      { src: 'Courier', fontStyle: 'normal', fontWeight: 400 },
      { src: 'Courier-Bold', fontStyle: 'normal', fontWeight: 700 },
      { src: 'Courier-Oblique', fontStyle: 'italic', fontWeight: 400 },
      { src: 'Courier-BoldOblique', fontStyle: 'italic', fontWeight: 700 },
    ],
  });
  Font.register({
    family: 'Times-Roman',
    fonts: [
      { src: 'Times-Roman', fontStyle: 'normal', fontWeight: 400 },
      { src: 'Times-Bold', fontStyle: 'normal', fontWeight: 700 },
      { src: 'Times-Italic', fontStyle: 'italic', fontWeight: 400 },
      { src: 'Times-BoldItalic', fontStyle: 'italic', fontWeight: 700 },
    ],
  });
}
